import React, { useState } from 'react';
import { Card, Form, Button, Alert, ListGroup, Badge, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { updateStudentPortalAccess } from '../../api/authApi';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ManagePortalAccess = ({ students, onAccessUpdated }) => {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [fileContent, setFileContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [tabKey, setTabKey] = useState('manual');
  const [csvText, setCsvText] = useState('');

  const handleSelectStudent = (email) => {
    if (selectedStudents.includes(email)) {
      setSelectedStudents(selectedStudents.filter(e => e !== email));
    } else {
      setSelectedStudents([...selectedStudents, email]);
    }
  };

  const handleSelectAll = (accessStatus) => {
    const filteredStudents = students
      .filter(student => student.can_access_portal === accessStatus)
      .map(student => student.email);
    
    setSelectedStudents(filteredStudents);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setFileContent(content);
      
      // Detect file type and parse accordingly
      if (file.name.endsWith('.csv')) {
        parseCSVFile(content);
      } else if (file.name.endsWith('.xlsx')) {
        parseExcelFile(content);
      } else {
        // Assume it's a text file with one email per line
        parseTextFile(content);
      }
    };
    
    if (file.name.endsWith('.xlsx')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const parseCSVFile = (content) => {
    Papa.parse(content, {
      complete: (results) => {
        const emails = extractEmailsFromParsedData(results.data);
        setSelectedStudents(emails);
        setCsvText(content); // Store for template download
      },
      error: (error) => {
        setError(`Error parsing CSV file: ${error.message}`);
      }
    });
  };

  const parseExcelFile = (content) => {
    try {
      const data = new Uint8Array(content);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsedData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      const emails = extractEmailsFromParsedData(parsedData);
      setSelectedStudents(emails);
    } catch (error) {
      setError(`Error parsing Excel file: ${error.message}`);
    }
  };

  const parseTextFile = (content) => {
    const lines = content.split(/[\r\n]+/);
    const emails = lines
      .map(line => line.trim())
      .filter(line => line.includes('@') && line.length > 0);
    
    setSelectedStudents(emails);
  };

  const extractEmailsFromParsedData = (data) => {
    // Flatten the array and extract anything that looks like an email
    const flatData = data.flat();
    return flatData
      .filter(item => item && typeof item === 'string' && item.includes('@'))
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const handleSubmit = async () => {
    if (selectedStudents.length === 0) {
      setError('Please select at least one student.');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      
      const response = await updateStudentPortalAccess(selectedStudents);
      
      if (response) {
        toast.success(`Access granted to ${response.message.split(' ')[2]} students.`);
        
        if (response.not_found && response.not_found.length > 0) {
          toast.warning(`${response.not_found.length} email(s) not found in the system.`);
        }
        
        // Reset selection
        setSelectedStudents([]);
        setFileContent('');
        
        // Callback to refresh student list
        if (onAccessUpdated) onAccessUpdated();
      }
    } catch (error) {
      console.error('Error updating access:', error);
      setError(error.message || 'Failed to update access. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadCSVTemplate = () => {
    // Create simple CSV with header
    const csv = "student_emails\nalice@pilani.bits-pilani.ac.in\nf20231185@pilani.bits-pilani.ac.in\njack@pilani.bits-pilani.ac.in";
    
    // Create and download the file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'portal_access_template.csv';
    link.click();
  };

  const downloadJSONTemplate = () => {
    // Create JSON template matching the API format
    const jsonData = {
      "student_emails": [
        "alice@pilani.bits-pilani.ac.in",
        "f20231185@pilani.bits-pilani.ac.in",
        "jack@pilani.bits-pilani.ac.in"
      ]
    };
    
    // Create and download the file
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'portal_access_template.json';
    link.click();
  };

  // const filteredStudents = students.filter(student => 
  //   !student.can_access_portal && student.registration_complete
  // );
  // const allRegisteredHaveAccess = students.every(student => 
  //   !student.registration_complete || student.can_access_portal
  // );

  const filteredStudents = students.filter(student => !student.can_access_portal);

// Check if all students have portal access
const allHaveAccess = students.every(student => student.can_access_portal);
  return (
    <Card>
      <Card.Header as="h5">Manage Portal Access</Card.Header>
      <Card.Body>
        <div className="mb-3">
          <Tabs
            activeKey={tabKey}
            onSelect={(k) => setTabKey(k)}
            className="mb-3"
          >
            <Tab eventKey="manual" title="Manual Selection">
              <div className="mb-3 d-flex justify-content-end">
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  className="me-2"
                  onClick={() => handleSelectAll(false)}
                >
                  Select All Without Access
                </Button>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => setSelectedStudents([])}
                >
                  Clear Selection
                </Button>
              </div>
              
              <div className="border rounded mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <ListGroup variant="flush">
                {allHaveAccess ? (
  <ListGroup.Item className="text-center text-muted">
    All students already have portal access
  </ListGroup.Item>
) : (
  filteredStudents.map(student => (
    <ListGroup.Item 
      key={student.email}
      className="d-flex justify-content-between align-items-center"
    >
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`student-${student.email}`}
          checked={selectedStudents.includes(student.email)}
          onChange={() => handleSelectStudent(student.email)}
        />
        <label className="form-check-label" htmlFor={`student-${student.email}`}>
          <div>
            <span>{student.name}</span>
            <div className="text-muted small">{student.email}</div>
          </div>
        </label>
      </div>
      <div>
        {student.registration_complete ? (
          <Badge bg="success" className="me-1">Registered</Badge>
        ) : (
          <Badge bg="warning" className="me-1">Incomplete</Badge>
        )}
        {!student.can_access_portal && (
          <Badge bg="danger">No Access</Badge>
        )}
      </div>
    </ListGroup.Item>
  ))
)}
                </ListGroup>
              </div>
            </Tab>
            
            <Tab eventKey="file" title="File Upload">
              <Form.Group className="mb-3">
                <Form.Label>Upload student emails (CSV, Excel, or Text file)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".txt,.csv,.xlsx"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  File should contain one email per line or in a column named "student_emails"
                  <Button 
                    variant="link" 
                    className="p-0 ms-1" 
                    onClick={downloadCSVTemplate}
                    disabled={uploading}
                  >
                    Download CSV template
                  </Button>
                  {' | '}
                  <Button 
                    variant="link" 
                    className="p-0" 
                    onClick={downloadJSONTemplate}
                    disabled={uploading}
                  >
                    Download JSON template
                  </Button>
                </Form.Text>
              </Form.Group>
              
              {fileContent && (
                <div className="mt-3">
                  <h6>Selected Students ({selectedStudents.length})</h6>
                  <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <ul className="list-unstyled mb-0">
                      {selectedStudents.map(email => (
                        <li key={email} className="small">
                          {email}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Tab>
          </Tabs>
          
          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          
          <div className="d-grid mt-3">
            <Button 
              variant="primary"
              onClick={handleSubmit}
              disabled={selectedStudents.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Granting Access...
                </>
              ) : `Grant Access to ${selectedStudents.length} Student(s)`}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ManagePortalAccess;